import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast, ToastOptions } from 'react-toastify'
import { NctsCountry } from 'config'
import { useTranslation } from 'react-i18next'
import { fetchTransitOperation } from 'features/declaration/transit-operation/api/fetchTransitOperation'
import { QueryKeys } from 'features/key-registry'
import { extractCountryFromDepartureOffice } from 'shared/ncts/common/classifier-util'
import { isAxiosError } from 'shared/api/isAxiosError'
import { ErrorResponse } from 'shared/api/parseAxiosTransitErrorResponse'
import { parseApiError } from 'shared/api/apiError'
import notifyServerError from 'shared/api/notifyServerError'
import DeclarationApi from 'routes/phase5/TransitOperationEditor/hooks/apiConfig'
import { fetchSigningDigest } from '../api/fetchSigningDigest'
import { submitSignedDeclaration } from '../api/submitSignedDeclaration'
import useSigningSession from './useSigningSession'

export type SigningStep = 'IDLE' | 'PREPARING' | 'SIGNING' | 'SUBMITTING' | 'SUCCESS' | 'ERROR'

function useSigning() {
  const { t } = useTranslation()
  const [step, setStep] = useState<SigningStep>('IDLE')
  const queryClient = useQueryClient()
  const [signingCountry, setSigningCountry] = useState<NctsCountry | null | undefined>(undefined)
  const { sessionCountry, getSession, clearSession } = useSigningSession()

  // The signer depends on the country, so a failed fetch fails the signing rather than guessing one.
  const resolveCountry = async (transitOperationId: number) => {
    const transitOperation = await queryClient.ensureQueryData({
      queryKey: QueryKeys.transitOperation.detail({ id: transitOperationId, isEns: false }),
      queryFn: ({ signal }) => fetchTransitOperation(transitOperationId, false, { signal }),
    })
    return extractCountryFromDepartureOffice(transitOperation.departureCustomsOffice ?? null)
  }

  const startSigning = async (transitOperationId: number, auditId: number) => {
    let sessionMethod: string | undefined

    try {
      setStep('PREPARING')
      setSigningCountry(undefined)
      const country = await resolveCountry(transitOperationId)
      setSigningCountry(country)
      const session = await getSession(country)
      sessionMethod = session.method

      const response = await fetchSigningDigest({
        transitOperationId,
        auditId,
        certificateDer: session.certificate,
      })

      setStep('SIGNING')
      const { signature, signatureAlgorithm } = await session.sign(response.dataToSign)

      setStep('SUBMITTING')
      await submitSignedDeclaration({
        transitOperationId,
        auditId,
        signatureBytes: signature,
        certificateDer: session.certificate,
        signatureAlgorithm,
      })
      queryClient.invalidateQueries({
        queryKey: [DeclarationApi.paths.transitOperationAudit.queryKeys.rootPath],
      })
      toast.success(t('common.submit', { context: 'success' }))

      setStep('SUCCESS')
    } catch (err) {
      const apiError = parseApiError(err)
      if (apiError.status !== 503 && apiError.reason !== 'networkError') {
        clearSession()
      }
      const sticky: ToastOptions = { autoClose: false, closeOnClick: false, draggable: false }
      if (apiError.status === 503) {
        toast.error(t('common.submit', { context: 'service_unavailable' }), sticky)
      } else if (apiError.reason === 'serverError' && apiError.traceId) {
        notifyServerError(apiError.traceId)
      } else if (apiError.status === 413) {
        toast.error(t('common.submit', { context: 'payload_too_large' }), sticky)
      } else if (isAxiosError<ErrorResponse>(err) && err.response?.data?.message) {
        toast.error(t('common.submit', { context: 'errorWithTraceId', traceId: err.response?.headers['x-trace-id'] ?? null }))
      } else {
        // Card and extension failures arrive here, and the providers put the only useful detail on
        // `code` - Web-eID's native app reports nothing beyond "operation failed" in its own dialog.
        // Losing it leaves an empty console and nothing to go on.
        const { code, message } = err as { code?: string; message?: string }
        // eslint-disable-next-line no-console
        console.error('[signing] failed', { method: sessionMethod, code, message })

        toast.error(code ? `${t('signing.error')} (${code})` : t('signing.error'), sticky)
      }
      setStep('ERROR')
    }
  }

  return {
    step,
    hasActiveSession: sessionCountry !== undefined && sessionCountry === signingCountry,
    startSigning,
  }
}

export default useSigning
